<template>
  <div v-if="!loading">
    <div>
      <div>
        <el-scrollbar wrap-style="max-height:70vh;">
          <el-table :data="fields" border style="width: 100%">
            <el-table-column prop="label" label="Label" width="180">
              <template slot-scope="scope">
                <div>
                  <label>
                    {{ scope.row.label }}
                  </label>
                </div>
              </template>
            </el-table-column>
            <el-table-column
              v-for="column in visibleFieldProperties"
              :label="column.label"
              :prop="column.value"
              :key="column.value"
              :width="column.width"
            >
              <!-- HEADER WITH BULK SELECT -->
              <template slot="header">
                <div class="flex items-center gap-2">
                  <span>{{ column.label }}</span>
                  <el-checkbox
                    v-if="['HIDE', 'REQUIRED', 'READ_ONLY'].includes(column.value)"
                    :indeterminate="bulkIndeterminate[column.value]"
                    v-model="bulkSelection[column.value]"
                    :key="column.value + '_' + bulkSelection[column.value]"
                    @change="handleBulkCheckboxChange(column.value, $event)"
                    size="mini"
                  />
                </div>
              </template>
              <template slot-scope="scope">
                <div v-if="customization[scope.row.key]">
                  <div v-if="column.value == 'LABEL'">
                    <el-input
                      v-model="customization[scope.row.key]['LABEL']"
                      placeholder="Value"
                    ></el-input>
                  </div>

                  <!-- HIDE -->
                  <div v-else-if="column.value == 'HIDE'">
                    <el-checkbox
                      v-model="customization[scope.row.key]['HIDE']"
                      :disabled="
                        restrictedFields[scope.row.key] || customization[scope.row.key]['REQUIRED']
                      "
                    ></el-checkbox>
                  </div>

                  <!-- REQUIRED -->
                  <div v-else-if="column.value == 'REQUIRED'">
                    <el-checkbox
                      v-model="customization[scope.row.key]['REQUIRED']"
                      :disabled="
                        restrictedFields[scope.row.key] ||
                        customization[scope.row.key]['HIDE'] ||
                        customization[scope.row.key]['READ_ONLY']
                      "
                    ></el-checkbox>
                  </div>

                  <!-- READ_ONLY -->
                  <div v-else-if="column.value == 'READ_ONLY'">
                    <el-checkbox
                      v-model="customization[scope.row.key]['READ_ONLY']"
                      :disabled="
                        restrictedFields[scope.row.key] || customization[scope.row.key]['REQUIRED']
                      "
                    ></el-checkbox>
                  </div>
                  <div v-else-if="column.value == 'INPUT_TYPE'" class="d-flex">
                    <el-select
                      v-model="customization[scope.row.key]['INPUT_TYPE']"
                      placeholder="Type"
                      :disabled="restrictedFields[scope.row.key]"
                    >
                      <el-option
                        v-for="op in getRelatedTypes(scope.row.key)"
                        :value="op"
                        :label="getFieldLabel(op)"
                        :key="op"
                      ></el-option>
                    </el-select>
                    <el-button
                      plain
                      size="mini"
                      v-if="
                        customization[scope.row.key]['INPUT_TYPE'] == 'SELECT' ||
                        customization[scope.row.key]['INPUT_TYPE'] == 'PHONE_COUNTRY_CODE'
                      "
                      icon="el-icon-setting"
                    ></el-button>
                  </div>
                  <div v-else-if="column.value == 'FONT_SIZE'">
                    <el-input-number
                      v-model="customization[scope.row.key]['FONT_SIZE']"
                      :min="8"
                      :max="72"
                      controls-position="right"
                      placeholder="Font size"
                      size="mini"
                    ></el-input-number>
                  </div>
                  <div v-else-if="column.value == 'FONT_COLOUR'">
                    <el-color-picker
                      v-model="customization[scope.row.key]['FONT_COLOUR']"
                      placeholder="Font color"
                      size="small"
                    ></el-color-picker>
                  </div>
                  <div v-else-if="column.value == 'LABEL_ALIGNMENT'">
                    <el-select
                      v-model="customization[scope.row.key]['LABEL_ALIGNMENT']"
                      placeholder="Select alignment"
                      size="mini"
                    >
                      <el-option label="To Left" value="left" />
                      <el-option label="To Center" value="center" />
                      <el-option label="To Right" value="right" />
                    </el-select>
                  </div>
                  <div v-else-if="column.value == 'FONT_STYLE'">
                    <el-select
                      v-model="customization[scope.row.key]['FONT_STYLE']"
                      placeholder="Select style"
                      size="mini"
                    >
                      <el-option label="Bold" value="1" />
                      <el-option label="Italic" value="2" />
                    </el-select>
                  </div>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </el-scrollbar>
      </div>
    </div>
    <span slot="footer" class="dialog-footer mt-2 mb-1" style="float: right; margin-right: 2%">
      <el-button @click="handleCancel()">Cancel</el-button>
      <el-button type="primary" @click="handleSave()">Save</el-button>
    </span>
  </div>
</template>

<script>
  import EntitiesHelper from '../../mixins/EntitiesHelper';
  import TemplateBuilderHelper from '@/mixins/templateBuilderHelper.js';

  import { mapGetters } from 'vuex';

  export default {
    data() {
      return {
        originalOptions: [],
        fields: [],
        fieldProperties: [
          {
            value: 'LABEL',
            label: 'Label',
            width: 180,
          },
          {
            value: 'HIDE',
            label: 'Hide',
            width: 120,
          },
          {
            value: 'REQUIRED',
            label: 'Required',
            width: 120,
          },
          {
            value: 'READ_ONLY',
            label: 'Read only',
            width: 120,
          },
          {
            value: 'INPUT_TYPE',
            label: 'Type',
            width: 180,
          },
          {
            value: 'FONT_SIZE',
            label: 'Font Size',
            width: 150,
          },
          {
            value: 'FONT_COLOUR',
            label: 'Font Color',
            width: 100,
          },
          {
            value: 'LABEL_ALIGNMENT',
            label: 'Label Alignment',
            width: 180,
          },
          {
            value: 'FONT_STYLE',
            label: 'Font Style',
            width: 150,
          },
        ],
        fieldChangeObject: [['SINGLE_LINE_TEXT', 'MULTI_LINE_TEXT', 'SELECT', 'NUMBER']],
        editingFieldKey: '',
        loading: true,
        restrictedFields: {},
        originalSettings: {},
        allowedFieldTypes: [
          'SINGLE_LINE_TEXT',
          'MULTI_LINE_TEXT',
          'SELECT',
          'NUMBER',
          'MULTI_SELECT',
          'YES_OR_NO',
          'PHONE_COUNTRY_CODE',
          'CURRENCY',
          'CHECKBOX',
          'ENTITY',
          'ENTITY_VARIABLE',
        ],
        hiddenColumns: [
          'LABEL',
          'INPUT_TYPE',
          'FONT_SIZE',
          'FONT_COLOUR',
          'FONT_STYLE',
          'LABEL_ALIGNMENT',
        ],
        originalCustomization: {},
        bulkSelection: {
          HIDE: false,
          REQUIRED: false,
          READ_ONLY: false,
        },
        bulkIndeterminate: {
          HIDE: false,
          REQUIRED: false,
          READ_ONLY: false,
        },
      };
    },
    mixins: [EntitiesHelper, TemplateBuilderHelper],
    props: ['templateData', 'customization', 'fromFormbuilder'],
    async mounted() {
      this.loading = true;
      this.fields = [];
      this.originalCustomization = this.customization;
      if (this.templateData) {
        this.fields = this.templateData;
      }
      this.buildDefaultCustomization();
    },
    computed: {
      ...mapGetters('navigationOpen', ['getIsMobile']),
      visibleFieldProperties() {
        return this.fieldProperties.filter((col) => !this.hiddenColumns.includes(col.value));
      },
    },
    methods: {
      handleBulkCheckboxChange(column, val) {
        this.$set(this.bulkSelection, column, val);
        this.$set(this.bulkIndeterminate, column, false);
        this.toggleColumnSelection(column, val);
        this.$nextTick(() => {
          this.refreshBulkCheckboxState(column);
        });
      },

      toggleColumnSelection(column, checked) {
        this.fields?.forEach((field) => {
          const key = field.key;
          const fieldCustomization = this.customization[key];
          if (
            field.isPrimary ||
            ['SINGLE_LINE_CONTENT', 'DIVISION'].includes(field.input_type) ||
            this.restrictedFields[key]
          ) {
            return;
          }
          if (!fieldCustomization) return;
          if (column === 'REQUIRED') {
            if (
              fieldCustomization.HIDE ||
              fieldCustomization.READ_ONLY ||
              this.restrictedFields[key]
            )
              return;
          }
          if ((column === 'HIDE' || column === 'READ_ONLY') && fieldCustomization.REQUIRED) {
            return;
          }
          this.$set(fieldCustomization, column, checked);
        });
        this.$nextTick(() => {
          this.refreshBulkCheckboxState(column);
        });
      },
      refreshBulkCheckboxState(column) {
        const eligibleFields = this.fields.filter(
          (f) =>
            !f.isPrimary &&
            !['SINGLE_LINE_CONTENT', 'DIVISION'].includes(f.input_type) &&
            !this.restrictedFields[f.key]
        );
        const total = eligibleFields.length;
        const selected = eligibleFields.filter((f) => this.customization[f.key]?.[column]).length;

        if (total === 0) {
          this.bulkSelection[column] = false;
          this.bulkIndeterminate[column] = false;
          return;
        }

        this.$set(this.bulkSelection, column, selected === total);
        this.$set(this.bulkIndeterminate, column, selected > 0 && selected < total);
      },

      handleSave() {
        this.editingFieldKey = '';
        this.originalOptions = [];
        this.$emit('updateCustomization', this.customization);
      },
      handleCancel() {
        this.customization = JSON.parse(JSON.stringify(this.originalCustomization));
        if (this.editingFieldKey && this.originalOptions.length >= 0) {
          this.customization[this.editingFieldKey].OPTIONS = [...this.originalOptions];
        }
        this.editingFieldKey = '';
        this.originalOptions = [];
        this.$emit('updateCustomization', this.customization);
      },
      getFieldLabel(string) {
        if (string) {
          string = string.toLowerCase().replaceAll('_', ' ');
          return string.charAt(0).toUpperCase() + string.slice(1);
        }
        return '';
      },
      getRelatedTypes(key) {
        if (key) {
          let field = this.fields.find((e) => e.key == key);
          if (this.fieldChangeObject.find((ops) => ops.includes(field.input_type))) {
            return this.fieldChangeObject.find((ops) =>
              ops.includes(field.inputType ? field.inputType : field.input_type)
            );
          }
          return [field.input_type];
        }
        return [];
      },
      getFieldCustomizationObj(field) {
        return {
          LABEL: field.label,
          HIDE: field?.properties?.filed_content === 'Hide',
          READ_ONLY: field?.properties?.filed_content === 'READ ONLY',
          REQUIRED: field?.validations?.required,
          INPUT_TYPE: field.input_type,
          OPTIONS: field.options || [],
          NEW_FIELD: false,
          FIELD_DATA: null,
          countryCodeSelector: false,
          countryDisable: false,
          countryCallingCode: '',
          defaultCountry: '',
          phoneNo: null,
        };
      },
      buildDefaultCustomization() {
        const validKeys = new Set(this.fields.map((f) => f.key));
        Object.keys(this.customization).forEach((key) => {
          if (!validKeys.has(key)) {
            this.$delete(this.customization, key);
            delete this.restrictedFields[key];
          }
        });
        this.fields.forEach((field) => {
          let obj = this.getFieldCustomizationObj(field);
          if (!this.customization[field.key]) {
            this.$set(this.customization, field.key, { ...obj });
          }
          if (field.isPrimary) {
            this.customization[field.key].REQUIRED = true;
            this.restrictedFields[field.key] = true;
          }
          if (['SINGLE_LINE_CONTENT', 'DIVISION'].includes(field.input_type)) {
            this.customization[field.key].READ_ONLY = true;
            this.restrictedFields[field.key] = true;
          }
        });
        if (this.templateData?.type == 'STANDARD') {
          this.fields.forEach((field) => {
            if (field?.validations?.required) {
              this.restrictedFields[field.key] = true;
            }
          });
        }
        this.loading = false;
      },
    },
    watch: {
      customization: {
        deep: true,
        handler() {
          ['HIDE', 'REQUIRED', 'READ_ONLY'].forEach((col) => this.refreshBulkCheckboxState(col));
        },
      },
    },
  };
</script>
